import { useState } from 'react';
import {
  Download,
  FileText,
  MessageCircle,
  PencilLine,
  Mic,
  Sprout,
  Check,
  BookOpen,
  ChevronRight,
} from 'lucide-react';
import { date, downloadRecord, time, words } from './api';
import { ErrorNotice } from './components';
import type { LearningEvent, Session } from './types';

const eventNames: Record<string, string> = {
  lesson_started: 'Opened the lesson',
  assignment_brief_reviewed: 'Reviewed the task brief and checklist',
  first_thoughts: 'Recorded first thoughts',
  work_saved: 'Saved a revision',
  mentor_exchange: 'Worked with Marvin',
  speech_confirmed: 'Reviewed spoken words',
  speech_transcribed: 'Recorded a transcript for review',
  work_submitted: 'Saved a copy for the independent check',
  independent_check_left: 'Returned to the working draft',
  assignment_submission_recorded: 'Student recorded submission status',
  independent_check: 'Completed an independent check',
  delayed_check_started: 'Started a delayed check',
  delayed_check: 'Completed the delayed check',
  teacher_feedback: 'Teacher left feedback',
  paste_detected: 'Pasted content',
  paste_retention_changed: 'Pasted passage edited',
  paste_reviewed: 'Student reviewed pasted material',
  assessment_policy_bound: 'Organisation policy linked',
  assessment_policy_recorded: 'AI rules confirmed for this assignment',
  assessment_policy_decision: 'Assessment AI permission checked',
  assessment_policy_reviewed: 'Student reviewed AI use',
  assessment_speech_played: 'Marvin reply read aloud',
  time_log_recorded: 'Time recorded',
  time_log_voided: 'Time entry corrected',
};
export function RecordDetails({
  session,
  revisions = true,
}: {
  session: Session;
  revisions?: boolean;
}) {
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const events = session.events
    .filter(
      (event) =>
        filter === 'all' ||
        (filter === 'mentor' ? event.type === 'mentor_exchange' : event.type === 'work_saved'),
    )
    .slice()
    .reverse();
  return (
    <div className="record-details">
      <p className="record-statement">
        This record shows contributions and assistance inside this workspace. It does not prove
        exclusive authorship or rule out outside help.
      </p>
      <div className="record-stats">
        <span>
          <strong>{session.events.filter((event) => event.type === 'work_saved').length}</strong>
          saved revisions
        </span>
        <span>
          <strong>
            {
              session.messages.filter(
                (item) => item.role === 'assistant' && item.kind !== 'orientation',
              ).length
            }
          </strong>
          Marvin responses
        </span>
        <span>
          <strong>{session.sourceIds.length}</strong>supporting resources
        </span>
      </div>
      <div className="record-downloads">
        <button
          className="secondary"
          onClick={() => void downloadRecord(session.id).catch((cause) => setError(cause.message))}
        >
          <Download size={16} />
          Download readable record
        </button>
        <button
          className="text-button"
          onClick={() =>
            void downloadRecord(session.id, 'json').catch((cause) => setError(cause.message))
          }
        >
          Full data (JSON)
        </button>
      </div>
      {error && <ErrorNotice message={error} />}
      <h3>Your starting point</h3>
      <p className="quote-block">
        {session.baseline?.answer ||
          (session.baseline?.skipped ? 'I’m not sure yet.' : 'First thoughts not recorded yet.')}
      </p>
      <h3>Evidence notes</h3>
      {session.lesson.sources
        .filter((source) => session.notes[source.id])
        .map((source) => (
          <div className="evidence-note" key={source.id}>
            <strong>
              <BookOpen size={14} />
              {source.title}
            </strong>
            <p>{session.notes[source.id]}</p>
          </div>
        ))}
      {!Object.values(session.notes).some(Boolean) && (
        <p className="muted">No source notes saved yet.</p>
      )}
      <div className="section-heading timeline-heading">
        <h3>The learning journey</h3>
        {revisions && (
          <select
            aria-label="Filter learning record"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            <option value="all">All activity</option>
            <option value="mentor">Marvin help</option>
            <option value="revisions">Draft revisions</option>
          </select>
        )}
      </div>
      <ol className="timeline compact-activity">
        {events.map((event) => (
          <TimelineEvent
            key={event.id}
            event={event}
            expanded={expanded.has(event.id)}
            onToggle={() =>
              setExpanded((previous) => {
                const next = new Set(previous);
                next.has(event.id) ? next.delete(event.id) : next.add(event.id);
                return next;
              })
            }
          />
        ))}
      </ol>
    </div>
  );
}
function TimelineEvent({
  event,
  expanded,
  onToggle,
}: {
  event: LearningEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  const unknown =
    event.lineage === 'unknown' ||
    (!event.lineage && (!event.origin || event.origin === 'external'));
  const detailed =
    event.type === 'mentor_exchange' ||
    event.type === 'work_saved' ||
    event.type === 'speech_transcribed' ||
    event.type === 'speech_confirmed';
  const preview =
    event.type === 'time_log_recorded' ? `${event.activity} · ${Math.round((event.seconds ?? 0) / 60)} min · ${event.source}` :
    event.type === 'time_log_voided' ? `Entry excluded from totals · ${event.reason}` :
    event.type === 'assignment_submission_recorded' ? (event.status==='submitted'?'Marked submitted by you':'Marked in progress by you') : event.type === 'work_saved'
      ? `Revision ${event.version} · ${event.words ?? words(event.draft || '')} words · ${event.inputMode || 'typed'} input`
      : event.type === 'paste_detected' && unknown
        ? 'External paste · lineage unknown'
        : (
            event.studentMessage?.text ||
            event.confirmedText ||
            event.originalTranscript ||
            event.answer ||
            event.title ||
            event.text ||
            event.explanation ||
            'View recorded details'
          ).replace(/\s+/g, ' ');
  return (
    <li>
      <button
        type="button"
        className="activity-toggle"
        aria-expanded={expanded}
        aria-controls={`activity-details-${event.id}`}
        onClick={onToggle}
      >
        <span className="activity-icon" aria-hidden="true">
          {event.type === 'mentor_exchange' ? (
            <MessageCircle size={13} />
          ) : event.type.startsWith('speech_') ? (
            <Mic size={13} />
          ) : event.type === 'work_saved' ? (
            <PencilLine size={13} />
          ) : (
            <Check size={13} />
          )}
        </span>
        <strong className="activity-name">
          {eventNames[event.type] || event.type.replaceAll('_', ' ')}
        </strong>
        <span className="activity-preview">{preview}</span>
        <time dateTime={event.at}>
          {date(event.at)} · {time(event.at)}
        </time>
        <ChevronRight className="activity-chevron" size={16} aria-hidden="true" />
      </button>
      <div id={`activity-details-${event.id}`} className="activity-details" hidden={!expanded}>
        {event.type.startsWith('assessment_') && <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(event, null, 2)}</pre>}
        {event.type === 'assignment_submission_recorded' && <p>{event.status==='submitted'?'Marked submitted by you. Your draft remains editable.':'Marked in progress by you. Earlier versions stay in history.'}</p>}
        {event.snapshot?.draft && <p className="student-prose">{event.snapshot.draft}</p>}
        {event.answer && <p className="student-prose">{event.answer}</p>}
        {event.title && <p>{event.title}</p>}
        {event.type === 'work_saved' && (
          <p className="muted">
            Revision {event.version} · {event.words} words · {event.inputMode || 'typed'} input
          </p>
        )}
        {event.type === 'paste_detected' && (
          <div className="quote-block">
            <strong>
              {unknown ? 'External paste — lineage unknown' : 'Recorded in-app transfer'}
            </strong>
            <p>
              {event.destination || 'Draft'} · {event.review}
            </p>
            <details>
              <summary>Original pasted text</summary>
              <p className="student-prose">{event.text}</p>
            </details>
            {event.sourceUrl && (
              <p>
                Provided reference: {event.sourceUrl}. A reference does not establish the origin of
                an external paste.
              </p>
            )}
          </div>
        )}
        {event.type === 'paste_reviewed' && (
          <p className="quote-block">
            {event.decision}: {event.explanation}
            <br />
            {event.reference}
            <br />
            Student-declared review. Original lineage flag retained.
          </p>
        )}
        {event.type === 'paste_retention_changed' && (
          <div className="quote-block">
            <strong>
              {event.status === 'removed'
                ? 'Full pasted text removed'
                : event.status === 'partial'
                  ? 'Pasted text partly removed'
                  : event.status === 'retained'
                    ? 'Full pasted text retained / restored'
                    : 'Paste tracking unavailable'}{' '}
              · Revision {event.version}
            </strong>
            {event.retainedCharacters !== undefined && (
              <p>
                {event.retainedCharacters} of {event.originalCharacters} characters retained
              </p>
            )}
            {!!event.removedSegments?.length && (
              <details>
                <summary>Removed original text</summary>
                {event.removedSegments.map((text, i) => (
                  <p className="student-prose" key={i}>
                    {text}
                  </p>
                ))}
              </details>
            )}
            {!!event.retainedSegments?.length && (
              <details>
                <summary>Retained original text</summary>
                {event.retainedSegments.map((text, i) => (
                  <p className="student-prose" key={i}>
                    {text}
                  </p>
                ))}
              </details>
            )}
            <p>The original paste remains in the audit.</p>
          </div>
        )}
        {event.type === 'mentor_exchange' && (
          <p className="muted">
            {event.mentorMessage?.kind} ·{' '}
            {event.mentorMessage?.provider === 'openai' ? 'Marvin · AI' : 'Authored guidance'}
          </p>
        )}
        {detailed && (
          <div>
            {event.type === 'mentor_exchange' ? (
              <div className="exchange-record">
                <p>
                  <strong>Student</strong>
                  {event.studentMessage?.text}
                </p>
                <p>
                  <strong>Marvin</strong>
                  {event.mentorMessage?.text}
                </p>
              </div>
            ) : event.type === 'work_saved' ? (
              <p className="student-prose">
                {event.draft || 'No draft text yet; notes or evidence selection changed.'}
              </p>
            ) : (
              <div className="exchange-record">
                <p>
                  <strong>Original transcription</strong>
                  {event.originalTranscript}
                </p>
                {event.type === 'speech_confirmed' ? (
                  <p>
                    <strong>Confirmed text</strong>
                    {event.confirmedText}
                  </p>
                ) : (
                  <p className="muted">
                    This transcription was awaiting confirmation when recorded. A separate
                    confirmation event records any text subsequently used.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
